import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireOnboarding } from '@/components/auth/RequireOnboarding';
import { ProjectProvider } from '@/components/projects/ProjectProvider';
import { ProjectLayout } from '@/components/projects/ProjectLayout';
import { PublicRoute } from '@/components/auth/PublicRoute';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { Skeleton } from '@/components/ui/skeleton';
import { usePageTracking } from '@/hooks/use-page-tracking';
import { setUserId } from '@/lib/analytics';
import { identifyClarityUser } from '@/lib/clarity';
import { useAuth } from '@/hooks/useAuth';

// Lazy-load auth pages for code splitting
const SignInPage = lazy(() =>
  import('@/pages/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import('@/pages/SignUpPage').then((m) => ({ default: m.SignUpPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const MagicLinkPage = lazy(() =>
  import('@/pages/MagicLinkPage').then((m) => ({ default: m.MagicLinkPage })),
);
const AuthCallbackPage = lazy(() =>
  import('@/pages/AuthCallbackPage').then((m) => ({
    default: m.AuthCallbackPage,
  })),
);
const SetNewPasswordPage = lazy(() =>
  import('@/pages/SetNewPasswordPage').then((m) => ({
    default: m.SetNewPasswordPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  })),
);
const OnboardingPage = lazy(() =>
  import('@/pages/OnboardingPage').then((m) => ({
    default: m.OnboardingPage,
  })),
);
const AcceptInvitePage = lazy(() =>
  import('@/pages/AcceptInvitePage').then((m) => ({
    default: m.AcceptInvitePage,
  })),
);
const FeatureDetailPage = lazy(() =>
  import('@/pages/FeatureDetailPage').then((m) => ({
    default: m.FeatureDetailPage,
  })),
);
const FoundationPage = lazy(() =>
  import('@/pages/FoundationPage').then((m) => ({
    default: m.FoundationPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({
    default: m.SettingsPage,
  })),
);
const ProjectsPage = lazy(() =>
  import('@/pages/ProjectsPage').then((m) => ({
    default: m.ProjectsPage,
  })),
);
const AdminMetricsPage = lazy(() =>
  import('@/pages/AdminMetricsPage').then((m) => ({
    default: m.AdminMetricsPage,
  })),
);

const DevComponentsPage = import.meta.env.DEV
  ? lazy(() => import('@/routes/dev/components'))
  : null;

function PageFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-64 space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

/**
 * Redirect from / and /dashboard to the /projects list page.
 */
function DashboardRedirect() {
  return <Navigate to="/projects" replace />;
}

/**
 * Invisible component that tracks page views and syncs user ID with GA4.
 * Must be rendered inside both BrowserRouter (for useLocation) and
 * AuthProvider (for useAuth).
 */
function PageTracker() {
  usePageTracking();

  const { user } = useAuth();

  useEffect(() => {
    setUserId(user?.id ?? null);
    if (user?.id) {
      identifyClarityUser(user.id);
    }
  }, [user?.id]);

  return null;
}

export function Router() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PageTracker />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public auth routes */}
            <Route
              path="/sign-in"
              element={
                <PublicRoute>
                  <SignInPage />
                </PublicRoute>
              }
            />
            <Route
              path="/sign-up"
              element={
                <PublicRoute>
                  <SignUpPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/magic-link"
              element={
                <PublicRoute>
                  <MagicLinkPage />
                </PublicRoute>
              }
            />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* Onboarding (RequireAuth only, NOT RequireOnboarding) */}
            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <OnboardingPage />
                </RequireAuth>
              }
            />

            {/* Invite acceptance (RequireAuth only, no onboarding required) */}
            <Route
              path="/invite/accept"
              element={
                <RequireAuth>
                  <AcceptInvitePage />
                </RequireAuth>
              }
            />

            {/* Protected routes (RequireAuth + RequireOnboarding) */}
            <Route
              path="/set-new-password"
              element={
                <RequireAuth>
                  <SetNewPasswordPage />
                </RequireAuth>
              }
            />

            {/* Projects list page — default landing after sign-in */}
            <Route
              path="/projects"
              element={
                <RequireAuth>
                  <RequireOnboarding>
                    <ProjectProvider>
                      <ProjectsPage />
                    </ProjectProvider>
                  </RequireOnboarding>
                </RequireAuth>
              }
            />

            {/* Project-scoped routes — all nested under /projects/:id */}
            <Route
              path="/projects/:id"
              element={
                <RequireAuth>
                  <RequireOnboarding>
                    <ProjectProvider>
                      <ProjectLayout />
                    </ProjectProvider>
                  </RequireOnboarding>
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="foundation" element={<FoundationPage />} />
              <Route path="settings/:tab?" element={<SettingsPage />} />
              <Route path="features/:featureId" element={<FeatureDetailPage />} />
            </Route>

            {/* Redirect / and /dashboard to /projects */}
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <RequireOnboarding>
                    <DashboardRedirect />
                  </RequireOnboarding>
                </RequireAuth>
              }
            />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <RequireOnboarding>
                    <DashboardRedirect />
                  </RequireOnboarding>
                </RequireAuth>
              }
            />

            {/* Admin routes (auth guard + email check inside page) */}
            <Route
              path="/admin/metrics"
              element={
                <RequireAuth>
                  <AdminMetricsPage />
                </RequireAuth>
              }
            />

            {/* Dev-only route */}
            {import.meta.env.DEV && DevComponentsPage && (
              <Route path="/dev/components" element={<DevComponentsPage />} />
            )}

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
